import React, { useRef, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import classnames from 'classnames'
import formattedCreditsWithHoursShort from '@Utils/FormattedCreditsWithHoursShort'
import useEditableName from '@Utils/UseEditableName'
import List from '@Images/projects_table_header_list.svg'
import Grid from '@Images/projects_table_header_grid.svg'
import Plus from '@Images/projects_table_new_project_plus.svg'
import Search from '@Images/projects_table_header_search.svg'
import CloseSearch from '@Images/projects_table_header_search_close.svg'
import Labels from '@Images/filters_dropdown_labels.svg'
import Button from '@SiteComponents/Button'
import FiltersDropdown from '@SiteComponents/FiltersDropdown'
import LabelsManager from '@SiteComponents/LabelsManager'

import {
  getCurrentWorkspaceName,
  getCurrentWorkspaceId,
  getSaving,
  getHasRenamedWorkspaceSuccessfully,
} from '@SiteModules/Workspace/selectors'

import {
  getTotalCount,
  getSearchText,
  getSelectedProjectTitles,
} from '@SiteModules/Project/selectors'

import {
  getCurrentAccountAvailableTotalCredits,
  getUserIsAccountAdmin,
  getCurrentAccountHasCurrentSubscription,
} from '@SiteModules/Account/selectors'

import {
  getLabels,
  getSelectedLabelIds,
  getSelectedLabelCaptions,
} from '@SiteModules/Label/selectors'

import {
  fetchProjects,
  setSearchText,
} from '@SiteModules/Project/actions'

import {
  renameWorkspace,
} from '@SiteModules/Workspace/actions'

import {
  changeSelectedLabels,
  setLabelSelection,
  createLabel,
} from '@SiteModules/Label/actions'

import './ProjectsTableHeader.scss'

interface ProjectsTableHeaderProps {
  readonly listActive: boolean
  openNewProjectPopup(): void
  openRenewSubscriptionPopup(): void
  setListActive(newListActive: boolean): void
}

const ProjectsTableHeader: React.FC<ProjectsTableHeaderProps> = props => {
  const dispatch = useDispatch()
  const { listActive } = props

  const [isFocused, setIsFocused] = useState(false)
  const currentWorkspaceName = useSelector(getCurrentWorkspaceName)
  const currentWorkspaceId = useSelector(getCurrentWorkspaceId)
  const totalCountOfProjects = useSelector(getTotalCount)
  const availableTotalCredits = useSelector(getCurrentAccountAvailableTotalCredits)
  const saving = useSelector(getSaving)
  const hasRenamedWorkspaceSuccessfully = useSelector(getHasRenamedWorkspaceSuccessfully)
  const userIsAccountAdmin = useSelector(getUserIsAccountAdmin)
  const labels = useSelector(getLabels)
  const selectedLabelIds = useSelector(getSelectedLabelIds)
  const selectedLabelCaptions = useSelector(getSelectedLabelCaptions)
  const searchText = useSelector(getSearchText)
  const selectedProjectTitles = useSelector(getSelectedProjectTitles)
  const currentAccountHasCurrentSubscription = useSelector(getCurrentAccountHasCurrentSubscription)

  const hasValidCurrentWorkspace = currentWorkspaceId >= 0
  const currentWorkspaceIdRef = useRef(currentWorkspaceId)
  const canEditWorkspaceName = userIsAccountAdmin

  useEffect(() => {
    currentWorkspaceIdRef.current = currentWorkspaceId
  }, [currentWorkspaceId])

  const onFocus = () => {
    setIsFocused(true)
  }

  const onBlur = () => {
    setIsFocused(false)
  }

  const handleRenameWorkspace = (name: string) => {
    dispatch(renameWorkspace({ workspaceId: currentWorkspaceIdRef.current, name }))
  }

  const changeSearchText = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchText = (e.target as HTMLInputElement).value

    handleSearchTextChange(newSearchText)
  }

  const handleSearchTextChange = (newSearchText: string) => {
    dispatch(setSearchText({ newSearchText }))
    dispatch(fetchProjects({
      page: 1,
      selectedLabels: selectedLabelIds,
      workspaceId: currentWorkspaceId,
      ...(newSearchText.length > 0 ? { searchText: newSearchText } : {}),
    }
    ))
  }

  const resetSearchText = () => {
    handleSearchTextChange('')
  }

  const {
    editing,
    editedName,
    inputWidth,
    nameContainerRef,
    inputRef,
    startEditing,
    changeName,
  } = useEditableName({
    name: currentWorkspaceName,
    saving,
    hasRenamedSuccessfully: hasRenamedWorkspaceSuccessfully,
    handleRename: handleRenameWorkspace,
    inputClassName: 'ProjectsTableHeader__workspace-name-input'
  })

  const handleSetListActive = () => {
    props.setListActive(true)
  }

  const handleSetGridActive = () => {
    props.setListActive(false)
  }

  const handleClickOnWorkspaceName = () => {
    if (canEditWorkspaceName) {
      startEditing()
    }
  }

  const handleChangeSelectedLabels = (e: React.ChangeEvent<HTMLInputElement>, labelId: number) => {
    dispatch(changeSelectedLabels({ labelId, selectLabel: e.target.checked }))
  }

  const handleResetLabelSelection = () => {
    dispatch(setLabelSelection({ labelIds: [] }))
  }

  const handleCreateLabel = (caption: string) => {
    dispatch(createLabel({ caption, workspaceId: currentWorkspaceId }))
  }

  const renderProjectsTableHeaderTop = () => {
    return (
      <div className="ProjectsTableHeader__top">
        {renderProjectsTableHeaderTopLeft()}
        {renderProjectsTableHeaderTopRight()}
      </div>
    )
  }

  const renderProjectsTableHeaderTopLeft = () => {
    return (
      <div className="ProjectsTableHeader__top-left">
        {renderWorkspaceName()}
        <div className="ProjectsTableHeader__number-of-projects">
          {totalCountOfProjects === 1 ? '1 project' : `${totalCountOfProjects} projects`}
        </div>
      </div>
    )
  }

  const renderWorkspaceName = () => {
    return (
      <React.Fragment>
        {editing ? (
          <input
            ref={inputRef}
            className="ProjectsTableHeader__workspace-name-input"
            style={{ width: inputWidth }}
            value={editedName}
            onChange={changeName}
            autoFocus
          />
        ) : (
          <div
            className={classnames(
              'ProjectsTableHeader__workspace-name',
              { 'ProjectsTableHeader__workspace-name-editable': canEditWorkspaceName }
            )}
            onClick={handleClickOnWorkspaceName}
            ref={nameContainerRef}
          >
            {currentWorkspaceName}
          </div>
        )}
      </React.Fragment>
    )
  }

  const renderProjectsTableHeaderTopRight = () => {
    return (
      <div className="ProjectsTableHeader__top-right">
        <div className="ProjectsTableHeader__credits-remaining">
          {`${formattedCreditsWithHoursShort(availableTotalCredits)} credits remaining`}
        </div>
      </div>
    )
  }

  const renderProjectsTableHeaderBottom = () => {
    return (
      <div className="ProjectsTableHeader__bottom">
        <div className="ProjectsTableHeader__bottom-left">
          {renderSearchInput()}
          {renderFilters()}
        </div>
        <div className="ProjectsTableHeader__bottom-right">
          {renderSwitch()}
          {renderNewProjectButton()}
        </div>
      </div>
    )
  }

  const renderSearchInput = () => {
    return (
      <div className={classnames(
        'ProjectsTableHeader__search',
        { 'ProjectsTableHeader__search-focused': isFocused }
      )}>
        <Search className="ProjectsTableHeader__search-icon"/>
        <CloseSearch
          className={classnames(
            'ProjectsTableHeader__search-icon-close',
            { 'ProjectsTableHeader__search-icon-close-hidden': !isFocused }
          )}
          onClick={resetSearchText}
        />
        <div className="ProjectsTableHeader__search-input-wrapper">
          <input
            placeholder="Search..."
            autoComplete="off"
            value={searchText}
            onChange={changeSearchText}
            onFocus={onFocus}
            onBlur={onBlur}
          />
        </div>
      </div>
    )
  }

  const renderFilters = () => {
    const hasNoLabels = labels.length === 0

    return (
      <FiltersDropdown
        categories={[{
          name: 'Labels',
          icon: <Labels />,
          selectedItemIds: selectedLabelIds,
          selectedItemCaptions: selectedLabelCaptions,
          itemManagerFactory: renderLabelsManager,
          resetItemSelection: handleResetLabelSelection
        }]}
        disabled={hasNoLabels}
      />
    )
  }

  const renderLabelsManager = () => {
    return (
      <LabelsManager
        labels={labels}
        selectedLabelIds={selectedLabelIds}
        selectedProjectTitles={selectedProjectTitles}
        indeterminateLabelIds={[]}
        changeSelectedLabels={handleChangeSelectedLabels}
        createLabel={handleCreateLabel}
      />
    )
  }

  const renderSwitch = () => {
    return (
      <div className="ProjectsTableHeader__switch">
        <div
          className={classnames(
            'ProjectsTableHeader__switch-bg-wrapper',
            { 'ProjectsTableHeader__switch-bg-right': !listActive }
          )}
        >
          <div className="ProjectsTableHeader__switch-bg" />
        </div>
        <div className="ProjectsTableHeader__switch-list" onClick={handleSetListActive}>
          <List />
          List
        </div>
        <div className="ProjectsTableHeader__switch-grid" onClick={handleSetGridActive}>
          <Grid />
          Grid
        </div>
      </div>
    )
  }

  const renderNewProjectButton = () => {
    return (
      <Button
        caption="New project"
        icon={<Plus />}
        onClick={currentAccountHasCurrentSubscription ? props.openNewProjectPopup : props.openRenewSubscriptionPopup}
        styleType="brand"
        size="large"
        iconRight
        disabled={!hasValidCurrentWorkspace}
      />
    )
  }

  return (
    <div className="ProjectsTableHeader">
      {renderProjectsTableHeaderTop()}
      {renderProjectsTableHeaderBottom()}
    </div>
  )
}

export default ProjectsTableHeader
